import DnsCheckerV3 from "./DnsCheckerV3";
import axios from "axios";
import { useState } from "react";
import { Globe, Search, CheckCircle2, XCircle, Loader2, ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { toast } from "sonner";
import { API } from "../../App";

const FLAG_EMOJI = {
  ES: "\u{1F1EA}\u{1F1F8}", FR: "\u{1F1EB}\u{1F1F7}", IT: "\u{1F1EE}\u{1F1F9}",
  DE: "\u{1F1E9}\u{1F1EA}", CA: "\u{1F1E8}\u{1F1E6}", US: "\u{1F1FA}\u{1F1F8}",
  MA: "\u{1F1F2}\u{1F1E6}", GB: "\u{1F1EC}\u{1F1E7}", BR: "\u{1F1E7}\u{1F1F7}",
  MX: "\u{1F1F2}\u{1F1FD}", NL: "\u{1F1F3}\u{1F1F1}", PT: "\u{1F1F5}\u{1F1F9}",
  AU: "\u{1F1E6}\u{1F1FA}", JP: "\u{1F1EF}\u{1F1F5}", IN: "\u{1F1EE}\u{1F1F3}",
  TR: "\u{1F1F9}\u{1F1F7}", SG: "\u{1F1F8}\u{1F1EC}", CH: "\u{1F1E8}\u{1F1ED}",
  SE: "\u{1F1F8}\u{1F1EA}",
};

// DNS Checker V4 - identical UX to V3 but hits /dns-check-v4 (separate logs/history)
export default function DnsCheckerV4({ token }) {
  const [host, setHost] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [showDetails, setShowDetails] = useState(false);

  const handleCheck = async () => {
    const cleaned = host.trim();
    if (!cleaned) return toast.error("Entrez un host");
    setLoading(true);
    setResult(null);
    try {
      const { data } = await axios.post(
        `${API}/user/tools/dns-check-v4`,
        { host: cleaned },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 45000 }
      );
      setResult(data);
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || "Erreur de verification DNS";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const pctColor = (pct) => (pct >= 80 ? "text-emerald-400" : pct >= 50 ? "text-amber-400" : "text-red-400");
  const barColor = (pct) => (pct >= 80 ? "bg-emerald-500" : pct >= 50 ? "bg-amber-500" : "bg-red-500");

  return (
    <div className="space-y-4" data-testid="dns-checker-v4">
      <p className="text-sm text-muted-foreground">
        DNS Checker V4 - Verification sur <strong>19 pays / 39 ISPs reels</strong>.
      </p>

      <div className="flex gap-2">
        <div className="relative flex-1">
          <Globe className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            data-testid="dns-v4-host-input"
            placeholder="example.com"
            value={host}
            onChange={(e) => setHost(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleCheck()}
            className="pl-10 bg-background border-border"
          />
        </div>
        <Button data-testid="dns-v4-check-btn" onClick={handleCheck} disabled={loading} className="gap-2 min-w-[140px]">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
          {loading ? "Analyse..." : "Check DNS"}
        </Button>
      </div>

      {result && (
        <div className="space-y-4 animate-in fade-in duration-300">
          <div
            className={`rounded-lg p-5 border ${
              result.success_pct >= 80
                ? "bg-emerald-500/10 border-emerald-500/30"
                : result.success_pct >= 50
                ? "bg-amber-500/10 border-amber-500/30"
                : "bg-red-500/10 border-red-500/30"
            }`}
            data-testid="dns-v4-global"
          >
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3">
                {result.success_pct >= 80 ? (
                  <CheckCircle2 className="h-6 w-6 text-emerald-400" />
                ) : result.success_pct >= 50 ? (
                  <CheckCircle2 className="h-6 w-6 text-amber-400" />
                ) : (
                  <XCircle className="h-6 w-6 text-red-400" />
                )}
                <span className="text-xl font-mono font-bold break-all">{result.domain}</span>
              </div>
              <div className="flex items-center gap-4">
                <span className="text-2xl font-bold font-mono">
                  {result.total_success}/{result.total_isps} <span className="text-sm text-muted-foreground font-normal">ISPs</span>
                </span>
                <span className={`text-3xl font-bold font-mono ${pctColor(result.success_pct)}`}>
                  {result.success_pct}%
                </span>
              </div>
            </div>
            <div className="w-full bg-muted/50 rounded-full h-2 mt-3">
              <div
                className={`h-2 rounded-full transition-all duration-700 ${barColor(result.success_pct)}`}
                style={{ width: `${result.success_pct}%` }}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
            {result.countries.map((c) => {
              const pct = c.total > 0 ? Math.round((c.success / c.total) * 100) : 0;
              return (
                <div
                  key={c.country}
                  className={`rounded-lg border p-2.5 ${
                    pct === 100
                      ? "border-emerald-500/30 bg-emerald-500/5"
                      : pct > 0
                      ? "border-amber-500/30 bg-amber-500/5"
                      : "border-red-500/30 bg-red-500/5"
                  }`}
                >
                  <div className="flex items-center gap-1.5 mb-1">
                    <span className="text-lg">{FLAG_EMOJI[c.flag] || c.flag}</span>
                    <span className="text-xs font-medium truncate">{c.country}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className={`text-xs font-bold font-mono ${pctColor(pct)}`}>
                      {c.success}/{c.total}
                    </span>
                    {pct === 100 ? (
                      <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" />
                    ) : pct > 0 ? (
                      <CheckCircle2 className="h-3.5 w-3.5 text-amber-400" />
                    ) : (
                      <XCircle className="h-3.5 w-3.5 text-red-400" />
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowDetails(!showDetails)}
            className="w-full gap-2 text-muted-foreground"
          >
            {showDetails ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            {showDetails ? "Masquer les details ISP" : "Detailed ISP Results"}
          </Button>

          {showDetails && (
            <div className="rounded-lg border border-border overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-muted/30 border-b border-border">
                    <th className="text-left p-2.5 font-medium text-muted-foreground">ISP</th>
                    <th className="text-left p-2.5 font-medium text-muted-foreground">Country</th>
                    <th className="text-center p-2.5 font-medium text-muted-foreground">Status</th>
                    <th className="text-right p-2.5 font-medium text-muted-foreground">Time</th>
                  </tr>
                </thead>
                <tbody>
                  {result.details.map((r, i) => (
                    <tr key={i} className="border-b border-border/50 hover:bg-muted/10">
                      <td className="p-2.5 font-mono text-xs">{r.isp}</td>
                      <td className="p-2.5">
                        <span className="text-xs">{FLAG_EMOJI[r.flag] || ""} {r.country}</span>
                      </td>
                      <td className="p-2.5 text-center">
                        {r.success ? (
                          <CheckCircle2 className="h-4 w-4 text-emerald-400 mx-auto" />
                        ) : (
                          <XCircle className="h-4 w-4 text-red-400 mx-auto" />
                        )}
                      </td>
                      <td className="p-2.5 text-right font-mono text-xs text-muted-foreground">
                        {r.time_ms >= 0 ? `${r.time_ms}ms` : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
